import React, { useRef, useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert, FormGroup, Label } from "reactstrap";
import SignatureCanvas from "react-signature-canvas";
import { useController } from "react-hook-form";
import { useThemeContext } from "../../Context/ThemeContext";
import { getDarkInput } from "../../Utils/otherFunctions";

const SignCanvasField = ({ 
  name, 
  control, 
  label, 
  className,
  isRequired,
  existingSign 
}) => {
  const signatureRef = useRef(null);
  const [isCanvasOpen, setIsCanvasOpen] = useState(false);
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const { themeContext } = useThemeContext();

  // Setup react-hook-form controller
  const {
    field: { onChange, value },
    fieldState
  } = useController({
    name,
    control,
    rules: { required: isRequired }
  });

  useEffect(() => {
    if (isCanvasOpen) {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
            setError(null);
          },
          (error) => {
            setError("Lokasi tidak dapat diakses: " + error.message);
          }
        );
      } else {
        setError("Geolocation tidak didukung di browser ini");
      }

      // Load existing signature if available
      if (value?.sign && signatureRef.current) {
        const img = new Image();
        img.src = value.sign;
        img.onload = () => {
          const canvas = signatureRef.current.getCanvas();
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        };
      }
    }
  }, [isCanvasOpen, value]);

  const handleClear = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleSave = () => {
    if (signatureRef.current) {
      const signature = signatureRef.current.getTrimmedCanvas().toDataURL("image/png");
      onChange({
        sign: signature,
        latitude: location?.latitude,
        longitude: location?.longitude
      });
      setIsCanvasOpen(false);
    }
  };

  return (
    <FormGroup className={className}>
      <div className="d-flex align-items-center mb-2">
        {label && (
          <Label className="bg-transparent mb-0 mr-3">
            {label}{" "}
            {isRequired && (
              <font className="ml-1" color="red">
                *
              </font>
            )}
          </Label>
        )}
        <button
          className="btn btn-link p-0"
          onClick={() => setIsCanvasOpen(true)}
          style={{ textDecoration: 'none' }}
          type="button"
        >
          Sign Here
        </button>
      </div>
      
      {value?.sign && (
        <div className="mt-2">
          <img src={value.sign} alt="signature" className="border rounded" height="100" />
        </div>
      )}

      {fieldState?.error && (
        <Label className="label-text text-danger mt-2">
          {fieldState.error.message}
        </Label>
      )}

      <Modal isOpen={isCanvasOpen} toggle={() => setIsCanvasOpen(false)} size="lg" fullscreen="lg">
        <ModalHeader toggle={() => setIsCanvasOpen(false)}>
          Tanda Tangan Digital
        </ModalHeader>
        <ModalBody className="d-flex flex-column justify-content-center align-items-center">
          {error && (
            <Alert color="warning" className="mb-3">
              {error}
            </Alert>
          )}
          {location && (
            <Alert color="info" className="align-self-stretch mb-3">
              Lokasi terdeteksi: {location.latitude}, {location.longitude}
            </Alert>
          )}
            <SignatureCanvas
              ref={signatureRef}
              canvasProps={{
                width: 600,
                height: 400,
                style: { border: "4px solid black", borderRadius: "4px" }
              }}
              minWidth={3}
              maxWidth={5}
            />
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleClear}>
            Hapus
          </Button>
          <Button color="success" onClick={handleSave}>
            Simpan
          </Button>
        </ModalFooter>
      </Modal>
    </FormGroup>
  );
};

export default SignCanvasField;